import type { CartModel, DiscountModel, ProductModel } from '@bytel/leviator';
import { FundingKey, JourneyType, PriceTypeEnum, ProductType } from '@bytel/leviator';

import { getAppConfig } from '@services/config';
import { isFtth3Clics } from '@services/ftth-3-clics';
import { getSessionVariable, setSessionVariable } from '@services/storage';
import { trackingManager } from '@services/tracking';

import type {
    CartProductTrackingType,
    CartTrackingType,
    OrderTrackingSnapshotType,
    PageViewTrackingType,
    TrackingType,
} from '@app-types/tracking';

import { noop } from '@helpers/try-catch';

import { MarketingType } from '@constants/plan';

const appConfig = getAppConfig();

export function formatTagText(text?: string) {
    if (!text) {
        return '';
    }
    return text
        .split(' ')
        .join('_')
        .split('&nbsp;')
        .join('_')
        .split('é')
        .join('e')
        .split('è')
        .join('e')
        .split('ê')
        .join('e')
        .split('à')
        .join('a');
}

export function sendTracking(isRenewal: boolean, data: TrackingType) {
    if (!appConfig.tracking.enabled) {
        return;
    }
    trackingManager.sendTracking(isRenewal, data).catch(noop);
}

export async function sendPageViewTracking(cart: CartModel | undefined, data: PageViewTrackingType) {
    const isRenewal = Boolean(cart?.getJourneyOfType(JourneyType.RENEWAL));
    const { universe, marketLine } = getCartTrackingContext(cart);
    const faiOffer = cart?.getJourneyOfType(JourneyType.FAI_ACQUISITION)?.getPrincipalProduct();

    if (!appConfig.tracking.enabled) {
        return;
    }

    await trackingManager
        .sendTracking(isRenewal, {
            ...data,
            variant: { versionPanier: 'v5', typeCommande: getTypeCommande(cart), ...data.variant },
            variables: { ftth3clics: faiOffer ? isFtth3Clics(faiOffer) : false, ...data.variables },
            universe: data.universe ?? universe,
            marketLine: data.marketLine ?? marketLine,
            cart: { ...buildCartTrackingPayload(cart), ...data.cart },
        })
        .catch(noop);
}

const ORDER_TRACKING_SNAPSHOT_KEY = 'order-tracking-snapshot';

export function saveOrderTrackingSnapshot(cart: CartModel | undefined) {
    if (!cart) {
        return;
    }

    const { universe, marketLine } = getCartTrackingContext(cart);
    const faiOffer = cart.getJourneyOfType(JourneyType.FAI_ACQUISITION)?.getPrincipalProduct();
    const renewalJourney = cart.getJourneyOfType(JourneyType.RENEWAL);

    const snapshot: OrderTrackingSnapshotType = {
        universe,
        marketLine,
        journey: renewalJourney ? 'Renouvellement' : 'Acquisition',
        variant: { versionPanier: 'v5', typeCommande: getTypeCommande(cart) },
        variables: { ftth3clics: faiOffer ? isFtth3Clics(faiOffer) : false },
        cart: buildCartTrackingPayload(cart) ?? {},
    };

    setSessionVariable(ORDER_TRACKING_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

export function readOrderTrackingSnapshot(cartId: string | undefined): OrderTrackingSnapshotType | undefined {
    const raw = getSessionVariable(ORDER_TRACKING_SNAPSHOT_KEY);

    if (!raw) {
        return undefined;
    }

    try {
        const snapshot = JSON.parse(raw) as OrderTrackingSnapshotType;
        return cartId && snapshot.cart?.idPanier !== cartId ? undefined : snapshot;
    } catch {
        return undefined;
    }
}
export function getTypeCommande(cart: CartModel | undefined): 'Precommande' | 'Commande' {
    const hasPreorder = cart?.journeys.some((journey) => journey.isPreorder()) ?? false;
    return hasPreorder ? 'Precommande' : 'Commande';
}

const fundingLabels: Record<FundingKey, string> = {
    [FundingKey.COMPTANT]: 'Comptant',
    [FundingKey.COFIDIS_X3]: '3xSansFrais',
    [FundingKey.COFIDIS_X12]: '12xCofidisPay',
    [FundingKey.COFIDIS_X24]: '24xCofidisPay',
    [FundingKey.YOUNITED_X3]: '3xYounited',
    [FundingKey.YOUNITED_X12]: '12xYounited',
    [FundingKey.YOUNITED_X24]: '24xYounited',
    [FundingKey.YOUNITED_X36]: '36xYounited',
    [FundingKey.SWAPPY_X30]: '30xSwappy',
    [FundingKey.SWAPPY_X33]: '33xSwappy',
    [FundingKey.SWAPPY_X36]: '36xSwappy',
    [FundingKey.PHENIX_X24]: '24xPhenix',
    [FundingKey.EDP]: 'FacilitePaiement24mois',
};

function buildModePaiement(cart: CartModel): string | undefined {
    const fundingKey = cart.funding?.key;

    if (!(cart.paymentMethod && fundingKey)) {
        return undefined;
    }

    const perimeter = fundingKey === FundingKey.EDP ? 'TelephoneUniquement' : 'IntegralitePanier';

    return `${perimeter}_${cart.paymentMethod}_${fundingLabels[fundingKey] ?? fundingKey}`;
}

function getLigneProduitNiv1(product: ProductModel): string | undefined {
    if (product.isType(ProductType.MAIN_FAI_OFFER)) {
        return 'Forfaits_FAI';
    }
    if (product.isType(ProductType.MAIN_MOBILE_OFFER)) {
        return 'Forfaits_mobile';
    }
    if (product.isType(ProductType.TERMINAL)) {
        return 'Terminaux';
    }
    if (product.isType(ProductType.ACCESSORY)) {
        return 'Accessoires';
    }
    if (product.isType(ProductType.OPTION) || product.isType(ProductType.PARTNER_OPTION)) {
        return 'Options';
    }
    if (product.isType(ProductType.INSURANCE)) {
        return 'Assurances';
    }
    if (product.isType(ProductType.FEES)) {
        return 'Autres';
    }
    if (product.isType(ProductType.MOBILE_BUYBACK)) {
        return 'Reprise_mobile';
    }
    return undefined;
}

function getLigneProduitNiv2(product: ProductModel): string | undefined {
    if (product.isType(ProductType.TERMINAL)) {
        return product.metadata?.name;
    }
    return undefined;
}

function getTypeForfait(locked: string | undefined): string | undefined {
    if (!locked) {
        return undefined;
    }
    return locked === '0' ? 'non_bloque' : 'bloque';
}

function buildProductTracking(product: ProductModel): CartProductTrackingType {
    return {
        name: product.metadata?.name ?? product.label,
        id: product.id,
        gencode: product.gencode,
        sku: product.metadata?.sku,
        price: product.price.priceWithPromotions,
        ligneProduitNiv1: getLigneProduitNiv1(product),
        ligneProduitNiv2: getLigneProduitNiv2(product),
        gamme: product.metadata?.commercialRange,
        range: product.metadata?.rangeNg,
        technology: product.metadata?.technology,
        type_id: product.metadata?.type,
        color: product.metadata?.color,
        capacity: product.metadata?.capacity,
        type_forfait: getTypeForfait(product.metadata?.locked),
        manufacturer: product.metadata?.brand,
    };
}

function buildPromotionTracking(product: ProductModel, discount: DiscountModel): CartProductTrackingType {
    const discountName = discount.label ?? discount.commercialLabel ?? discount.id;
    const name = `Remise ${discountName}`;

    return {
        id:
            discount.marketingType === MarketingType.HOME
                ? (product.metadata?.name ?? product.label)
                : (discount.code ?? discountName),
        name,
        gencode: name,
        sku: name,
        price: discount.amount === undefined ? undefined : -discount.amount,
        type_id: 'remise',
        ligneProduitNiv1: 'Promo',
    };
}

export function buildCartTrackingPayload(cart: CartModel | undefined): CartTrackingType | undefined {
    if (!cart) {
        return undefined;
    }

    const isFai = Boolean(cart.getJourneyOfType(JourneyType.FAI_ACQUISITION));
    const cartType = isFai ? 'fai' : 'sowo';

    const immediateTotal = cart.getPriceByType(PriceTypeEnum.IMMEDIATE)?.priceWithPromotions;
    const monthlyTotal = cart.getPriceByType(PriceTypeEnum.MONTHLY)?.priceWithPromotions;

    const hasInsurance = cart.journeys.some((journey) => journey.getProductsByType(ProductType.INSURANCE).length > 0);

    const shippingLabel = cart.journeys.map((j) => j.shipping?.label).find(Boolean);

    const cartProducts: CartProductTrackingType[] = cart.journeys.flatMap((journey) =>
        journey.products.flatMap((product) => [
            buildProductTracking(product),
            ...product.appliedDiscounts.map((discount) => buildPromotionTracking(product, discount)),
        ]),
    );

    return {
        idPanier: cart.id,
        total: {
            base: immediateTotal,
            recurring: monthlyTotal,
        },
        type: cartType,
        canalVente: 'WEB',
        assurance: hasInsurance,
        ...(shippingLabel ? { modeLivraison: shippingLabel } : {}),
        ...(cartProducts.length > 0 ? { produits: cartProducts } : {}),
        modePaiement: buildModePaiement(cart),
    };
}

export function getCartTrackingContext(cart: CartModel | undefined): {
    universe: 'Boutique_fai' | 'Boutique_mobile' | 'Boutique_mixte' | 'Boutique_web';
    marketLine: 'Fai' | 'Mobile' | 'Mixte' | 'Web';
} {
    const hasFai = Boolean(cart?.getJourneyOfType(JourneyType.FAI_ACQUISITION));
    const hasMobile =
        Boolean(cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION)) ||
        Boolean(cart?.getJourneyOfType(JourneyType.RENEWAL));

    if (hasFai && hasMobile) {
        return { universe: 'Boutique_mixte', marketLine: 'Mixte' };
    }
    if (hasMobile) {
        return { universe: 'Boutique_mobile', marketLine: 'Mobile' };
    }
    if (hasFai) {
        return { universe: 'Boutique_fai', marketLine: 'Fai' };
    }
    return { universe: 'Boutique_web', marketLine: 'Web' };
}
